import React from "react";
import { useQuery } from "@tanstack/react-query";
import KnownWords from "./KnownWords";
import StatPill from "./StatPill";
// Colored "🦜 parrot" wordmark for the onLight header (courses + dictionary),
// matching the
// iOS AppTopBar centerLogo `require("@assets/AppLogo.png")` (AppTopBar.tsx:67).
// NOTE: the web client's own AppLogo.png is the square app ICON, not the wordmark,
// so we use the iOS wordmark copied verbatim as parrot-wordmark.png.
import logoOnLight from "../../assets/parrot-wordmark.png";
// Home header logo over the dark sky. Uses the new refined parrot bird
// (parrot-bird.png — the same art as the start/login screens) instead of the
// old flat parrot-logo-variant art, so the bird is consistent everywhere.
// Home center logo — the iOS AppTopBar home logo (parrot-logo-variant.png),
// matching client-native AppTopBar.tsx:67 instead of the web-only bird art.
import logoOnDark from "../../assets/parrot-logo-variant.png";
// iOS streak badge renders the fire.png image asset (AppTopBar.tsx:76-79),
// not the platform 🔥 emoji — copied verbatim so the pill is pixel-identical.
import fireIcon from "../../assets/fire.png";
import { haptic } from "@/lib/haptic";
import { useNavigate } from "react-router-dom";
import { callWithAuth, getUserStats } from "@/api/Api";
import { ActivePage } from "../NavBar/NavBar";
import { useStreakCalendar } from "../Streak/StreakCalendarContext";

interface HeaderProps {
  activePage: ActivePage;
}

/**
 * Home/shell top header.
 *
 * iOS ref: client-native/src/features/top-headers/AppTopBar.tsx — three-part
 * row over the sky: streak pill (🔥 {dayStreak}) left, centered 74px logo,
 * 📖 words badge right. dayStreak defaults to 1, never 0 (AppTopBar.tsx:43).
 *
 * iOS renders AppTopBar on BOTH the home/learn surface (WeeklyCalendar.tsx:218,
 * default `variant="onDark"` over the sky) AND the courses surface
 * (CoursesScreen.tsx:358/372, `variant="onLight"`). So both tabs get the same
 * three-part bar — streak pill left, centered wordmark, words badge right — just
 * themed differently. The remaining tabs (dictionary/profile) do NOT use
 * AppTopBar on iOS (profile has its own gear/share header). Profile suppresses
 * this header entirely (HomeLayout); dictionary falls through to the
 * left-aligned wordmark-only branch below.
 */
const Header: React.FC<HeaderProps> = ({ activePage }) => {
  const nav = useNavigate();
  // iOS AppTopBar onStreakPress opens the streak calendar overlay
  // (WeeklyCalendar.tsx:218-221), it does NOT navigate.
  const { open: openStreakCalendar } = useStreakCalendar();

  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
  });
  // iOS floors the streak at 1 (AppTopBar.tsx:43 `storeDayStreak || 1`).
  const dayStreak = userStatsQuery.data?.dayStreak || 1;

  // Tabs that render the iOS AppTopBar. Home is onDark (over the sky), courses
  // is onLight (over the light course list); the variant drives pill/wordmark
  // theming. Other tabs fall through to the plain-title header below.
  const isHome = activePage === "learn";
  const isCourses = activePage === "courses";
  const isAppTopBar = isHome || isCourses;
  // Home sits over the iOS deep-blue SkyBackground → onDark (translucent white
  // pills + white logo, AppTopBar default variant); courses is onLight over the
  // light course list (CoursesScreen.tsx:358/372).
  const barTheme: "onDark" | "onLight" = isHome ? "onDark" : "onLight";
  const centerLogo = isHome ? logoOnDark : logoOnLight;

  return (
    // Always transparent — the fixed wrapper in HomeLayout paints the bar's
    // background (the tab's own background color, iOS AppTopBar-style). Plain
    // divs (not IonHeader/IonToolbar) so the single @ionic/react usage — which
    // pulled ~107 @ionic/core modules into the entry chunk — is gone.
    <div className="w-full h-full" style={{ background: "transparent" }}>
      <div className="w-full max-w-[600px] mx-auto h-full flex flex-row items-center justify-between gap-1 p-3 pr-2 relative">
        {isAppTopBar ? (
          <>
            {/* Streak pill — left (iOS AppTopBar). */}
            <StatPill
              icon={
                <img
                  src={fireIcon}
                  alt=""
                  aria-hidden
                  style={{ width: 16, height: 16, objectFit: "contain" }}
                />
              }
              value={dayStreak}
              theme={barTheme}
              onClick={() => {
                haptic.soft();
                openStreakCalendar();
              }}
              ariaLabel={`${dayStreak} day streak`}
            />
            {/* Center wordmark — absolutely centered in a 74px box to match
                  iOS AppTopBar centerLogo (74×74, resizeMode contain;
                  AppTopBar.tsx:108-110). Absolute centering means it never
                  pushes the side pills; object-contain keeps the wide wordmark
                  inside the box so it can't collide with them at 320px. */}
            <img
              src={centerLogo}
              alt="Parrot"
              onClick={() => {
                haptic.soft();
                nav("/home/learn");
              }}
              // 74×74 object-contain box on both tabs to match iOS AppTopBar
              // centerLogo (AppTopBar.tsx:108-110).
              className="absolute left-1/2 -translate-x-1/2 object-contain cursor-pointer h-[74px] w-[74px]"
            />
            {/* Words badge — right. */}
            <KnownWords theme={barTheme} />
          </>
        ) : (
          // Dictionary (ios-7): just the colored "parrot" wordmark, left-
          // aligned over the sky — no centered page title, no words badge,
          // no small beak icon. Same wordmark family as Home, in its colored
          // (orange) form rather than the white-on-sky variant.
          <img
            src={logoOnLight}
            alt="Parrot"
            onClick={() => {
              haptic.soft();
              nav("/home/learn");
            }}
            className="h-7 w-auto object-contain cursor-pointer"
          />
        )}
      </div>
    </div>
  );
};

export default Header;
